/**
 * Die Wörter eines **evcc-Standorts** (HM-165) — das ganze Haus, wie evcc es
 * regelt. Gedeutet hat die API (`EvccSiteState`); hier steht, wie es heißt.
 *
 * **Was nicht gemeldet ist, steht nicht da.** Eine Zeile entsteht nur aus
 * einem Feld mit Datenpunkt und Wert — wie am Speicher (HM-122). Eine
 * Preisgrenze ohne eingerichteten Schalter ist keine Grenze, und die API
 * liefert sie dann ohne Wert.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import type { Row } from "./device-report.js";

export type EvccSiteState = ApiComponents["schemas"]["EvccSiteState"];
export type BatteryMode = NonNullable<EvccSiteState["batteryMode"]["value"]>;
export type BatteryModePreset = NonNullable<EvccSiteState["batteryModeExternal"]["value"]>;

export const BATTERY_MODE_LABELS: Readonly<Record<BatteryMode, string>> = {
  unknown: "unbekannt",
  normal: "normal",
  hold: "halten",
  charge: "laden",
  hold_charge: "halten und laden",
  discharge_grid: "ins Netz entladen",
};

/** Die Vorgabe in der Reihenfolge, in der man wählt — „keine" zuerst, sie ist der Normalfall. */
export const BATTERY_MODE_PRESETS: readonly { value: BatteryModePreset; label: string }[] = [
  { value: "none", label: "Keine Vorgabe — evcc entscheidet" },
  { value: "normal", label: "Normal" },
  { value: "hold", label: "Halten" },
  { value: "charge", label: "Laden" },
  { value: "hold_charge", label: "Halten und laden" },
  { value: "discharge_grid", label: "Ins Netz entladen" },
];

const percent = (ratio: number) => `${Math.round(ratio * 100)} %`;
const cent = (eur: number) =>
  `${(eur * 100).toLocaleString("de-DE", { maximumFractionDigits: 1 })} ct/kWh`;
const watt = (w: number) => `${Math.round(w).toLocaleString("de-DE")} W`;
const yesNo = (flag: boolean) => (flag ? "ja" : "nein");

/** Was gerade gilt — die Tarife und was der Hausakku tut. */
export function evccSiteRows(s: EvccSiteState): Row[] {
  const rows: Row[] = [];
  const add = <T>(label: string, field: { value: T | null }, text: (value: T) => string) => {
    if (field.value !== null) rows.push([label, text(field.value)]);
  };
  add("Netzpreis jetzt", s.tariffGridEurPerKwh, cent);
  add("Einspeisevergütung jetzt", s.tariffFeedInEurPerKwh, cent);
  add("CO₂ im Netz jetzt", s.tariffCo2GPerKwh, (g) => `${Math.round(g)} g/kWh`);
  add("Betriebsart des Hausakkus", s.batteryMode, (mode) => BATTERY_MODE_LABELS[mode]);
  add("Hausakku lädt aus dem Netz", s.batteryGridChargeActive, yesNo);
  add("Hausakku entlädt ins Netz", s.batteryGridDischargeActive, yesNo);
  add("Hausakku für Ladepunkte ab", s.bufferSoc, percent);
  add("Hausakku startet Laden ab", s.bufferStartSoc, percent);
  add("Hausakku hat Vorrang bis", s.prioritySoc, percent);
  add("Regelreserve", s.residualPowerW, watt);
  add("Einspeisegrenze", s.gridExportLimitW, watt);
  return rows;
}
